import { CreateCommentRequest, CreateCommentResponse, EditReactionRequest, GetCommentResponse, ObjectId } from '../../types';
import { buildQueryString, del, emptyPut, get, post, put } from './api';

import accountInfo from '../state/account-info';
import topicData from '../state/topic-data';
import { undefinedIfEmpty } from '../utils';

export async function createPointComment(pointId: string, comment: string,is_public: boolean, parent_comment_id?: string, username?: string): Promise<Comment> {
    let parent_comment_oid: ObjectId | undefined;
    const name = username ?? undefinedIfEmpty(accountInfo.account?.full_name) ?? 'Anonymous User';
    if (parent_comment_id) {
        parent_comment_oid = { $oid: parent_comment_id };
    }
    const body: CreateCommentRequest = {
        content: comment,
        parent_comment_id: parent_comment_oid,
        is_public,
        username: name,
        send_notification: !topicData.isWriter,
    };
    const response = await fetch(`/api/comments/point/${pointId}`, post(body));
    if (!response.ok) {
        throw new Error('An error occurred while creating the comment.');
    }
    return await response.json();
}

// only the original author can edit the comment. The owner of the item can, however, delete the comment or hide it.
export async function update_comment(comment_id: string, comment: CreateCommentRequest): Promise<CreateCommentResponse> {
    const response = await fetch(`/api/comments/${comment_id}`, put(comment));
    if (!response.ok) {
        throw new Error('An error occurred while updating the comment.');
    }
    return await response.json();
}

export async function changeCommentPublicStatus(comment_id: string): Promise<CreateCommentResponse> {
    const response = await fetch(`/api/comments/${comment_id}/public?`, emptyPut());
    if (!response.ok) {
        throw new Error('An error occurred while updating the comment.');
    }
    return await response.json();
}

export async function deleteComment(comment_id: string): Promise<void> {
    const response = await fetch(`/api/comments/${comment_id}`, del());
    if (!response.ok) {
        throw new Error('An error occurred while deleting the comment.');
    }
}

export async function editCommentReaction(comment_id: string, reaction_change: EditReactionRequest): Promise<void> {
    const response = await fetch(`/api/comments/${comment_id}/reaction`, post(reaction_change));
    if (!response.ok) {
        throw new Error('An error occurred while updating the comment.');
    }
}

export async function getPointComments(pointId: string, key?: string): Promise<GetCommentResponse[]> {
    const queryString = buildQueryString({
        master_key: key,
    });
    const response = await fetch(`/api/comments/point/${pointId}${queryString}`, get());
    if (!response.ok) {
        throw new Error('An error occurred while fetching the comments.');
    }
    return await response.json();
}

// Internal Challenge Notes API Calls
export async function createInternalChallengeNote(pointId: string, comment: string, activated_challenge_id: string, activated_challenge_key: string, username?: string): Promise<CreateCommentResponse> {
    const body: CreateCommentRequest = {
        content: comment,
        is_public: false, // Internal notes are always private
        username: username ?? 'Internal',
        send_notification: false,
    };
    const queryString = buildQueryString({
        activated_challenge_key,
        activated_challenge_id,
    });
    const response = await fetch(`/api/challenge_note/${pointId}${queryString}`, post(body));
    if (!response.ok) {
        throw new Error('An error occurred while creating the internal note.');
    }
    return await response.json();
}

export async function updateInternalChallengeNote(comment_id: string, comment: string, activated_challenge_id: string, activated_challenge_key: string, username?: string): Promise<CreateCommentResponse> {
    const body: CreateCommentRequest = {
        _id: { $oid: comment_id },
        content: comment,
        is_public: false, // Internal notes are always private
        send_notification: false,
        username: username ?? 'Internal',
    };
    const queryString = buildQueryString({
        activated_challenge_key,
        activated_challenge_id,
    });
    const response = await fetch(`/api/challenge_note/${comment_id}${queryString}`, put(body));
    if (!response.ok) {
        throw new Error('An error occurred while updating the internal note.');
    }
    return await response.json();
}

export async function deleteInternalChallengeNote(comment_id: string, activated_challenge_id: string, activated_challenge_key: string): Promise<void> {
    const queryString = buildQueryString({
        activated_challenge_key,
        activated_challenge_id,
    });
    const response = await fetch(`/api/challenge_note/${comment_id}${queryString}`, del());
    if (!response.ok) {
        throw new Error('An error occurred while deleting the internal note.');
    }
}

export async function getInternalChallengeNotes(pointId: string, activated_challenge_id: string, activated_challenge_key: string): Promise<GetCommentResponse[]> {
    const queryString = buildQueryString({
        activated_challenge_key,
        activated_challenge_id,
    });
    const response = await fetch(`/api/challenge_note/${pointId}${queryString}`, get());
    if (!response.ok) {
        throw new Error('An error occurred while fetching the comments.');
    }
    return await response.json();
}